import { Dispatch, FormEvent, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import { BadgeTypeConstants, ProjectPermissionConstants, StandardConstants, UserType } from "../../../../../constants";
import { BadgeData, BasicDetailsData, LocationData, ProjectDatesData, SelectData } from "../../../../../models";
import { updateProjectDetails, UpdateProjectDetailsRequest } from "../../../../../service/project";
import {
  GetProjectDetailsResponse,
  getStandardSpecificSchemaDetails,
  searchStandards,
  searchVerifiers,
} from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { formatCoordinates, isFalsyExceptZero, splitCoordinates, useIsLoadingWrapper } from "../../../../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../../../../widget";
import { ProjectContext } from "../ProjectContext";
import { StandardSpecificDefinition } from "./components/StandardSpecificSection/types";

interface UseOverviewReturnData {
  currentUserType: UserType;
  projectDetails: GetProjectDetailsResponse | undefined;
  projectDates: ProjectDatesData | undefined;
  setProjectDates: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  currentStatus: string | undefined;
  errors: ServiceError[];
  isReadOnly: boolean;
  setIsReadOnly: (value: boolean) => void;
  isEditable: boolean;
  basicDetailsData: BasicDetailsData;
  setBasicDetailsData: Dispatch<SetStateAction<BasicDetailsData>>;
  locationData: LocationData;
  setLocationData: Dispatch<SetStateAction<LocationData>>;
  validatorData: SelectData;
  badgeData: BadgeData[];
  certificateData: BadgeData[];
  standardSpecificDefinition: StandardSpecificDefinition | undefined;
  isNoStandard: boolean;
  standardOptions: SelectData;
  selectedStandardUuid: string | undefined;
  showStandardChangeModal: boolean;
  onStandardSelect(standardUuid: string): void;
  onCancelStandardChange(): void;
  onSubmit(e: FormEvent<HTMLFormElement> | undefined): Promise<void>;
  onCancel(): void;
  showUpdateGroupVVBModal: boolean;
  setShowUpdateGroupVVBModal: Dispatch<SetStateAction<boolean>>;
  showEditDatesPanel: boolean;
  setShowEditDatesPanel: Dispatch<SetStateAction<boolean>>;
  isOnSubmitLoading: boolean;
  shouldRefreshProjectDetails: () => void;
}

export const useOverview = (): UseOverviewReturnData => {
  const { currentUserType } = useAuth();
  const {
    projectDetails,
    setProjectDetails,
    projectDates,
    setProjectDates,
    hasProjectPermission,
    shouldRefreshProjectDetails,
  } = useContext(ProjectContext);
  const [isOnSubmitLoading, setIsOnSubmitLoading] = useState(false);
  const [standardSpecificDefinition, setStandardSpecificDefinition] = useState<
    StandardSpecificDefinition | undefined
  >();
  const [basicDetailsData, setBasicDetailsData] = useState<BasicDetailsData>({
    displayName: projectDetails?.displayName,
    name: projectDetails?.name,
    registryId: projectDetails?.projectReference?.externalReference,
    totalArea: projectDetails?.areaNetHa?.toString(),
    validatorUuid: projectDetails?.validator?.uuid,
    projectType: projectDetails?.projectType.displayName,
    projectStatus: projectDetails?.status,
    projectTypeUuid: projectDetails?.projectType.uuid,
  });

  const [locationData, setLocationData] = useState<LocationData>({
    addressCountryCode: projectDetails?.addressCountryCode,
    addressRegion: projectDetails?.addressRegion,
    gridReference: projectDetails?.locationGridReference,
    ...splitCoordinates(projectDetails?.locationCoordinates),
    locationUuid: projectDetails?.projectLocation?.uuid || null,
    locationRowVersion: projectDetails?.projectLocation?.rowVersion || null,
    boundaryData: projectDetails?.projectLocation?.boundaryData || null,
  });

  const [submittedValues, setSubmittedValues] = useState<{
    basicDetailsData: BasicDetailsData;
    locationData: LocationData;
  }>({
    basicDetailsData: {
      displayName: projectDetails?.displayName,
      name: projectDetails?.name,
      registryId: projectDetails?.projectReference?.externalReference,
      totalArea: projectDetails?.areaNetHa?.toString(),
      validatorUuid: projectDetails?.validator?.uuid,
      projectType: projectDetails?.projectType.displayName,
      projectStatus: projectDetails?.status,
      projectTypeUuid: projectDetails?.projectType.uuid,
    },
    locationData: {
      addressCountryCode: projectDetails?.addressCountryCode,
      addressRegion: projectDetails?.addressRegion,
      gridReference: projectDetails?.locationGridReference,
      ...splitCoordinates(projectDetails?.locationCoordinates),
      locationUuid: projectDetails?.projectLocation?.uuid || null,
      locationRowVersion: projectDetails?.projectLocation?.rowVersion || null,
      boundaryData: projectDetails?.projectLocation?.boundaryData || null,
    },
  });

  const [isReadOnly, setIsReadOnly] = useState(true);

  const [validatorData, setValidatorData] = useState<SelectData>([]);
  const [showUpdateGroupVVBModal, setShowUpdateGroupVVBModal] = useState(false);

  const [showEditDatesPanel, setShowEditDatesPanel] = useState(false);

  const [currentRowVersion, setCurrentRowVersion] = useState<number>(projectDetails?.rowVersion || 1);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  const currentStandardUuid = projectDetails?.standard?.uuid;
  const isNoStandard = currentStandardUuid === StandardConstants.NO_STANDARD_UUID;

  // Default the combo box to the project's current standard ("No standard"), so leaving it
  // untouched is a valid no-op. Only picking a different, established standard is a change.
  const [standardOptions, setStandardOptions] = useState<SelectData>([]);
  const [selectedStandardUuid, setSelectedStandardUuid] = useState<string | undefined>(currentStandardUuid);
  const [showStandardChangeModal, setShowStandardChangeModal] = useState(false);

  useEffect(() => {
    setSelectedStandardUuid(currentStandardUuid);
  }, [currentStandardUuid]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (projectDetails?.standardSpecificSchemaUuid) {
        await getStandardSpecificSchemaDetails({
          standardSpecificSchemaUuid: projectDetails.standardSpecificSchemaUuid,
        }).then((response) => {
          if (response.status === Status.Success && response.data) {
            setStandardSpecificDefinition(response.data.definition);
          }
        });
      } else {
        // Clear any previously loaded definition so navigating to a project without a
        // schema (e.g. between projects in a group) does not render stale standard-specific fields.
        setStandardSpecificDefinition(undefined);
      }
    };
    fetchData();
  }, [projectDetails?.standardSpecificSchemaUuid]);

  useEffect(() => {
    setBasicDetailsData({
      displayName: projectDetails?.displayName,
      name: projectDetails?.name,
      registryId: projectDetails?.projectReference?.externalReference,
      totalArea: projectDetails?.areaNetHa?.toString(),
      validatorUuid: projectDetails?.validator?.uuid,
      projectType: projectDetails?.projectType.displayName,
      projectStatus: projectDetails?.status,
      projectTypeUuid: projectDetails?.projectType.uuid,
    });
    setLocationData({
      addressCountryCode: projectDetails?.addressCountryCode,
      addressRegion: projectDetails?.addressRegion,
      gridReference: projectDetails?.locationGridReference,
      ...splitCoordinates(projectDetails?.locationCoordinates),
      locationUuid: projectDetails?.projectLocation?.uuid || null,
      locationRowVersion: projectDetails?.projectLocation?.rowVersion || null,
      boundaryData: projectDetails?.projectLocation?.boundaryData || null,
    });
    setCurrentRowVersion(projectDetails?.rowVersion || 1);
  }, [projectDetails]);

  const fetchData = useCallback(async () => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.projectTypes = {
      some: {
        uuid: {
          operator: "eq",
          value: basicDetailsData.projectTypeUuid,
        },
      },
    };

    await searchVerifiers({
      filter: {
        results: filterCriteria,
      },
      paging: {
        limit: 10,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [],
    }).then((response) => {
      const valData =
        response.data?.results?.map((x) => {
          return { key: x.uuid, value: x.displayName };
        }) ?? [];
      setValidatorData(valData);
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // REQ-017: Load established standards so a No standard project can be upgraded.
  // Only offer standards that support the project's current project type.
  const currentProjectTypeName = projectDetails?.projectType.displayName;

  useEffect(() => {
    // Only load the options when the combo box is actually shown (edit mode, No standard project).
    if (isReadOnly || !isNoStandard || !currentProjectTypeName) {
      return;
    }

    const fetchStandards = async (): Promise<void> => {
      const response = await searchStandards({
        paging: { afterCursor: null, beforeCursor: null, limit: 50 },
        filter: {
          results: {
            canCreateNewProjects: { operator: "eq", value: true },
            uuid: { operator: "neq", value: StandardConstants.NO_STANDARD_UUID },
            // Only standards that offer the project's current project type with a usable schema.
            // `some` is not in the typed filter shape, so this matches the create-project flow's cast.
            // `standardSpecificSchemaId: neq null` keeps out standards with no usable schema for that type.
            projectTypes: {
              some: {
                displayName: { operator: "eq", value: currentProjectTypeName },
                standardSpecificSchemaId: { operator: "neq", value: null },
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
          },
        },
        sort: [{ key: "results.displayName", direction: "asc" }],
      });

      if (response.status === Status.Success && response.data?.results) {
        // Keep "No standard" as the first option so the current state stays selectable (a no-op).
        setStandardOptions([
          { key: StandardConstants.NO_STANDARD_UUID, value: StandardConstants.NO_STANDARD_DISPLAY_NAME },
          ...response.data.results
            .filter((standard) => standard.displayName)
            .map((standard) => ({ key: standard.uuid, value: standard.displayName as string })),
        ]);
      }
    };

    fetchStandards().catch(() => {
      setStandardOptions([
        { key: StandardConstants.NO_STANDARD_UUID, value: StandardConstants.NO_STANDARD_DISPLAY_NAME },
      ]);
    });
  }, [isReadOnly, isNoStandard, currentProjectTypeName]);

  // Selecting a standard only stages the choice; it is committed as part of Save (onSubmit).
  const onStandardSelect = (standardUuid: string): void => {
    setSelectedStandardUuid(standardUuid);
  };

  // Dismiss the assign-standard confirmation without committing; the staged selection is kept.
  const onCancelStandardChange = (): void => {
    setShowStandardChangeModal(false);
  };

  const updateProjectDetailsContext = (updatedRowVersion: number): void => {
    if (projectDetails) {
      const locationCoordinates = formatCoordinates(locationData.latitude, locationData.longitude);

      setProjectDetails({
        ...(projectDetails as GetProjectDetailsResponse),
        areaNetHa: isFalsyExceptZero(basicDetailsData.totalArea) ? null : Number(basicDetailsData.totalArea),
        displayName: basicDetailsData.displayName || "",
        name: basicDetailsData.name || "",
        projectReference: {
          ...projectDetails.projectReference,
          externalReference: basicDetailsData.registryId || "",
        },
        addressCountryCode: locationData.addressCountryCode || null,
        locationCoordinates,
        locationGridReference: locationData.gridReference || null,
        validator: {
          ...projectDetails.validator,
          uuid: basicDetailsData.validatorUuid || "",
        },
        rowVersion: updatedRowVersion,
      });
    }
  };

  const onSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement> | undefined): Promise<void> => {
    if (e) e.preventDefault();
    setErrors([]);
    if (projectDetails) {
      if (
        projectDetails.validator?.uuid !== basicDetailsData.validatorUuid &&
        projectDetails.group != null &&
        showUpdateGroupVVBModal === false
      ) {
        setShowUpdateGroupVVBModal(true);
        return;
      }
      // Confirm assignment only when a different, established standard has been selected.
      // Leaving the combo box on the current "No standard" value is a valid no-op.
      const isStandardChanged = !!selectedStandardUuid && selectedStandardUuid !== currentStandardUuid;
      if (isStandardChanged && showStandardChangeModal === false) {
        setShowStandardChangeModal(true);
        return;
      }
      const locationCoordinates = formatCoordinates(locationData.latitude, locationData.longitude);

      const request: UpdateProjectDetailsRequest = {
        rowVersion: currentRowVersion,
        displayName: basicDetailsData.displayName || "",
        projectUuid: projectDetails.uuid,
        areaNetHa: isFalsyExceptZero(basicDetailsData.totalArea) ? null : Number(basicDetailsData.totalArea),
        validatorUuid: basicDetailsData.validatorUuid || null,
        addressCountryCode: locationData.addressCountryCode || null,
        locationCoordinates,
        locationGridReference: locationData.gridReference || "",
        dates: null,
        name: basicDetailsData.name ?? null,
        externalReference: basicDetailsData.registryId ?? null,
        addressRegion: locationData.addressRegion ?? null,
        standardUuid: isStandardChanged ? selectedStandardUuid : undefined,
      };

      const updateRes = await updateProjectDetails(request);

      if (updateRes.status === Status.Success && updateRes.data) {
        Toast.success({ message: "Project details changed successfully" });
        setCurrentRowVersion(updateRes.data.rowVersion);
        setErrors([]);
        updateProjectDetailsContext(updateRes.data.rowVersion);
        setSubmittedValues({
          basicDetailsData: basicDetailsData as BasicDetailsData,
          locationData,
        });
        shouldRefreshProjectDetails();
        setShowUpdateGroupVVBModal(false);
        setShowStandardChangeModal(false);
        setSelectedStandardUuid(currentStandardUuid);
        setIsReadOnly(true);
      }

      if (updateRes.status === Status.Error) {
        setErrors(updateRes.errors || []);
        setShowUpdateGroupVVBModal(false);
        setShowStandardChangeModal(false);
      }
    }
  }, setIsOnSubmitLoading);

  const onCancel = (): void => {
    setBasicDetailsData(submittedValues.basicDetailsData);
    setLocationData(submittedValues.locationData);
    setSelectedStandardUuid(currentStandardUuid);
    setIsReadOnly(true);
  };

  // Keep row versions in sync
  useEffect(() => {
    if (projectDates && projectDetails) {
      if (projectDates.rowVersion > projectDetails.rowVersion) {
        setProjectDetails({
          ...projectDetails,
          rowVersion: projectDates?.rowVersion,
        });
      } else if (projectDates.rowVersion < projectDetails.rowVersion) {
        setProjectDates({
          ...projectDates,
          rowVersion: projectDetails?.rowVersion,
        });
      }
    }
  }, [[projectDates?.rowVersion, projectDetails?.rowVersion]]);

  return {
    currentUserType,
    projectDetails,
    projectDates,
    setProjectDates,
    currentStatus: projectDetails?.status,
    isReadOnly,
    setIsReadOnly,
    isEditable: hasProjectPermission(ProjectPermissionConstants.WRITE_PROJECT_DETAILS),
    errors,
    basicDetailsData,
    setBasicDetailsData,
    locationData,
    setLocationData,
    onSubmit,
    onCancel,
    validatorData,
    badgeData: projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_BADGE) || [],
    certificateData: projectDetails?.badges.filter((b) => b.type === BadgeTypeConstants.BADGE_TYPE_CERTIFICATION) || [],
    standardSpecificDefinition,
    isNoStandard,
    standardOptions,
    selectedStandardUuid,
    showStandardChangeModal,
    onStandardSelect,
    onCancelStandardChange,
    isOnSubmitLoading,
    showUpdateGroupVVBModal,
    setShowUpdateGroupVVBModal,
    showEditDatesPanel,
    setShowEditDatesPanel,
    shouldRefreshProjectDetails,
  };
};
